import { format, parse, formatDuration, intervalToDuration, set, isAfter } from "date-fns";

// Locales
import enUsLocale from "date-fns/locale/en-US";
import esLocale from "date-fns/locale/es";

import { UserResponse } from "../models/response";
import { store } from "../store";
import { KeyValue } from "../models/common";
import { CultureEnum } from "./enum/CultureEnum";
import { userUtcOffsetMilliseconds } from "@/main/services/utilsUser";
import { getTranslateByKey, isNullUndefined } from "./utils";
import { toast } from "react-toastify";
import { CustomError } from "./class/customError";

export function formatDate(data: Date, formatDate: string): string {
	const date = new Date(data);
	const formattedDate = format(date, formatDate);
	return formattedDate;
}

export function getPatternDate() {
	const user: UserResponse | null = store.getState().user.currentUser;
	if (user && user.patternDate) {
		return user.patternDate;
	}

	return "yyyy/dd/MM";
}

export function getPatternDateTime() {
	const user: UserResponse | null = store.getState().user.currentUser;
	if (user && user.idCulture) {
		switch (user.idCulture) {
			case CultureEnum.enUS:
				return "MM/dd/yyyy - hh:mm a";
			case CultureEnum.esMX:
				return "dd/MM/yyyy - HH:mm";
		}
	}

	return "MM/dd/yyyy hh:mm a";
}

export function convertToDate(date?: string | Date): Date | undefined {
	if (!date) {
		return undefined;
	}
	if (typeof date === "string") {
		return (date = parse(date, "yyyy-MM-dd HH:mm:ss.SSS", new Date()));
	}
	return date;
}

export function getUtcNow() {
	const dateNow = new Date();
	return new Date(
		dateNow.getUTCFullYear(),
		dateNow.getUTCMonth(),
		dateNow.getUTCDate(),
		dateNow.getUTCHours(),
		dateNow.getUTCMinutes(),
		dateNow.getUTCSeconds(),
	);
}

export function getDateNowTimeZoneUser() {
	const offset = userUtcOffsetMilliseconds();
	const dateUTCNow = getUtcNow();
	return new Date(dateUTCNow.getTime() + offset);
}

export function convertStringToDate(date: string | Date): Date {
	if (typeof date === "string") {
		return new Date(date);
	}
	return date;
}

export function getDateWithoutTime(date: Date) {
	let dateValue = new Date();
	if (date) {
		dateValue = convertStringToDate(date);
	}
	dateValue.setHours(0, 0, 0, 0);
	return dateValue;
}

export function convertLocalDateToUTCDate(date: Date) {
	return new Date(
		date.getUTCFullYear(),
		date.getUTCMonth(),
		date.getUTCDate(),
		date.getUTCHours(),
		date.getUTCMinutes(),
		date.getUTCSeconds(),
	);
}

export function removeSecAndMsOfDate(date: Date) {
	const newDate = date.setHours(date.getHours(), date.getMinutes(), 0, 0);

	return newDate;
}

export function convertMillisecondsInDate(milliseconds: number) {
	const convertMillisecondsInDate = new Date(1970, 1, 1).setMilliseconds(milliseconds);
	return convertMillisecondsInDate;
}

export function convertDateInMilliseconds(date: Date) {
	const convertDateInMilliseconds: number = date - new Date(1970, 1, 1).getMilliseconds();
	return convertDateInMilliseconds;
}

export function dateIsValid(date: any) {
	return date instanceof Date && !_.isNaN(date);
}

export function getDiffBetweenDate(startDate: any, endDate: any) {
	if (!startDate || !endDate) return String("");

	const start = new Date(startDate);

	const end = new Date(endDate);

	const isValidStartDate = dateIsValid(start);

	const isValidEndDate = dateIsValid(end);

	if (!isValidStartDate || !isValidEndDate) return String("");

	if (start > end) return String("");

	const totalDuration = intervalToDuration({
		start,

		end,
	});

	return formatDuration(totalDuration, {
		format: ["years", "months", "days"],
		delimiter: ", ",
		locale: ptBrLocale,
	});
}

export function validateMinDate(date?: Date | string | null) {
	if (date && typeof date === "string" && date === "0001-01-01T00:00:00") {
		return null;
	}
	return date;
}

export function getMonths(): KeyValue[] {
	return [
		{ key: 1, value: "1" },
		{ key: 2, value: "2" },
		{ key: 3, value: "3" },
		{ key: 4, value: "4" },
		{ key: 5, value: "5" },
		{ key: 6, value: "6" },
		{ key: 7, value: "7" },
		{ key: 8, value: "8" },
		{ key: 9, value: "9" },
		{ key: 10, value: "10" },
		{ key: 11, value: "11" },
		{ key: 12, value: "12" },
	];
}

export function getHoursCustom(): KeyValue[] {
	return [
		{ key: 0, value: "00:00" },
		{ key: 1, value: "01:00" },
		{ key: 2, value: "02:00" },
		{ key: 3, value: "03:00" },
		{ key: 4, value: "04:00" },
		{ key: 5, value: "05:00" },
		{ key: 6, value: "06:00" },
		{ key: 7, value: "07:00" },
		{ key: 8, value: "08:00" },
		{ key: 9, value: "09:00" },
		{ key: 10, value: "10:00" },
		{ key: 11, value: "11:00" },
		{ key: 12, value: "12:00" },
		{ key: 13, value: "13:00" },
		{ key: 14, value: "14:00" },
		{ key: 15, value: "15:00" },
		{ key: 16, value: "16:00" },
		{ key: 17, value: "17:00" },
		{ key: 18, value: "18:00" },
		{ key: 19, value: "19:00" },
		{ key: 20, value: "20:00" },
		{ key: 21, value: "21:00" },
		{ key: 22, value: "22:00" },
		{ key: 23, value: "23:00" },
	];
}

export function getYears(): KeyValue[] {
	const years: KeyValue[] = [];
	const currentYear = new Date().getFullYear();
	const INITIAL_YEAR = 1900;
	for (let i = currentYear; i >= INITIAL_YEAR; i--) {
		years.push({ key: i, value: String(i) });
	}
	return years;
}

export function getDays(): KeyValue[] {
	const days: KeyValue[] = [];
	const FINAL_DAY = 31;
	for (let i = 1; i <= FINAL_DAY; i++) {
		days.push({ key: i, value: String(i) });
	}
	return days;
}

export function changeRangeDate(change: string, range: number) {
	const date = new Date();
	let dateChangedRange;
	if (change === "max") {
		date.setDate(date.getDate() + range);
		dateChangedRange = date;
	} else if (change === "min") {
		date.setDate(date.getDate() - range);
		dateChangedRange = date;
	} else {
		dateChangedRange = date;
	}

	return dateChangedRange;
}

export const formatHours = (date: Date, culture: string): string => {
	const formattedHours = format(date, "hh:mm", { locale: getLocale(culture) });
	const period = format(set(date, {}), "aaa", {});
	return `${formattedHours} ${period.toUpperCase()}`;
};

export function formatDateToISOString(date: Date) {
	const year = date.getFullYear();
	const month = String(date.getMonth() + 1).padStart(2, "0");
	const day = String(date.getDate()).padStart(2, "0");
	const hours = String(date.getHours()).padStart(2, "0");
	const minutes = String(date.getMinutes()).padStart(2, "0");

	return `${year}-${month}-${day}T${hours}:${minutes}`;
}

export function getLocale(culture: string) {
	switch (culture) {
		case CultureEnum.enUS:
			return enUsLocale;
		case CultureEnum.esMX:
			return esLocale;
		default:
			return enUsLocale;
	}
}

export function isPeriodGreater(startDate: Date, finalDate: Date, parameterToDiference: number): boolean {
	if (isNullUndefined(startDate) || isNullUndefined(finalDate)) return false;

	startDate.setHours(0, 0, 0, 0);
	finalDate.setHours(0, 0, 0, 0);

	const differenceInMiliseconds = finalDate.getTime() - startDate.getTime();
	const differenceInDays = differenceInMiliseconds / (1000 * 3600 * 24) + 1;
	return differenceInDays > parameterToDiference;
}

export function isTimePeriodGreater(startDate: Date, finalDate: Date, parameterToDifferenceInHours: number): boolean {
	const differenceInMilliseconds = finalDate.getTime() - startDate.getTime();

	const differenceInHours = differenceInMilliseconds / (1000 * 60 * 60);

	return differenceInHours > parameterToDifferenceInHours;
}

export function initalDateForm(interval: number | null = null) {
	const newDate = getDateNowTimeZoneUser();

	if (interval) {
		newDate.setDate(newDate.getDate() - (interval - 1));
	}

	return newDate;
}

const isDecimal = (n: number) => n % 1 !== 0;
export function getRangeInitialFinalDate(rangeDays: number) {
	const rangeBetween = rangeDays / 2;
	const initialRange = isDecimal(rangeBetween) ? Math.floor(rangeBetween) : rangeBetween;
	const finalRange = isDecimal(rangeBetween) ? Math.round(rangeBetween) : rangeBetween;

	return {
		initialRange,
		finalRange,
	};
}
export function finalDateForm(interval: number | null = null) {
	const newDate = getDateNowTimeZoneUser();

	if (interval) {
		newDate.setDate(newDate.getDate() + interval);
	}

	return newDate;
}

export function validateFormatDate(changeDate: number | Date): boolean {
	if (isNaN(Number(changeDate)) || isNullUndefined(changeDate) || String(changeDate) === "Invalid Date") {
		return false;
	}
	return true;
}

export type TypeKeysRequiredValidationsDate = "isInvalidStartDate" | "isInvalidFinalDate";
export type TypeKeysUniqueValidationsDate =
	| "finalDateGreaterThanCurrentDate"
	| "startDateGreaterThanFinalDate"
	| "rangeBetweenDays";
export type TypeKeysValidationsDate = TypeKeysRequiredValidationsDate | TypeKeysUniqueValidationsDate;

type IObjValidationsDate<T extends string | number | symbol> = Record<T, { condition: boolean; message: string }>;
export function getAllKeysIgnoreValidationsDate(keysToUsed: TypeKeysValidationsDate[] = []): TypeKeysValidationsDate[] {
	const allKeys: TypeKeysValidationsDate[] = [
		"finalDateGreaterThanCurrentDate",
		"isInvalidFinalDate",
		"isInvalidStartDate",
		"rangeBetweenDays",
		"startDateGreaterThanFinalDate",
	];
	const keysToIgnore = allKeys.filter((key) => !keysToUsed.includes(key));

	return keysToIgnore;
}
export function validateFormDate(
	startDate: Date | null,
	finalDate: Date | null,
	parameterTypeValue: number,
	ignoreKeyValidations: TypeKeysValidationsDate[] = [],
) {
	const error = getFormDateValidationError(startDate, finalDate, parameterTypeValue, ignoreKeyValidations);
	if (error) {
		error.messages.forEach((message) => toast.error(message));
		throw new CustomError(error.messages.join("\n"), error.code);
	}
	return true;
}

export function getFormDateValidationError(
	startDate: Date | null,
	finalDate: Date | null,
	parameterTypeValue: number,
	ignoreKeyValidations: TypeKeysValidationsDate[] = [],
): { messages: string[]; code: string } | null {
	//#region Error messages with agroup of validations (with array of messages)
	const messages = [];
	const OBJ_REQUIRED_VALIDATIONS_DATE: IObjValidationsDate<TypeKeysRequiredValidationsDate> = {
		isInvalidStartDate: {
			condition: isNullUndefined(startDate) || String(startDate) === "Invalid Date",
			message: getTranslateByKey("information.startDateSinceIsRequired", true),
		},
		isInvalidFinalDate: {
			condition: isNullUndefined(finalDate) || String(finalDate) === "Invalid Date",
			message: getTranslateByKey("information.finalDateUntilIsRequired", true),
		},
	};

	for (const keyRequired of Object.keys(OBJ_REQUIRED_VALIDATIONS_DATE) as TypeKeysRequiredValidationsDate[]) {
		let skipValidation: boolean = false;

		if (ignoreKeyValidations && ignoreKeyValidations.length > 0) {
			skipValidation = ignoreKeyValidations.includes(keyRequired);
		}

		if (
			!skipValidation &&
			OBJ_REQUIRED_VALIDATIONS_DATE[keyRequired].condition &&
			OBJ_REQUIRED_VALIDATIONS_DATE[keyRequired].message
		) {
			messages.push(OBJ_REQUIRED_VALIDATIONS_DATE[keyRequired].message);
		}
	}

	if (messages.length > 0) {
		return { messages, code: "VALIDATIONS_DATE - REQUIRED" };
	}
	//#endregion

	const startDateString = !isNullUndefined(startDate) ? startDate.toUTCString() : "";
	const finalDateString = !isNullUndefined(finalDate) ? finalDate.toUTCString() : "";
	const startDateValue = new Date(startDateString);
	const finalDateValue = new Date(finalDateString);
	const currentDateValue = getDateNowTimeZoneUser();

	startDateValue.setHours(0, 0, 1, 0);
	finalDateValue.setHours(23, 59, 59, 999);
	currentDateValue.setHours(23, 59, 59, 999);

	//#region Error messages unique validations (without array of messages)
	const obj_validations_Date: IObjValidationsDate<TypeKeysUniqueValidationsDate> = {
		startDateGreaterThanFinalDate: {
			condition: isAfter(startDateValue, finalDateValue),
			message: getTranslateByKey("information.theStartDateMustNotBeGreaterThanTheFinalDate", true),
		},
		finalDateGreaterThanCurrentDate: {
			condition: isAfter(finalDateValue, currentDateValue),
			message: getTranslateByKey("information.thefinaldatemustnotbelaterthanthecurrentday", true),
		},
		rangeBetweenDays: {
			condition: isPeriodGreater(startDateValue, finalDateValue, parameterTypeValue),
			message: getTranslateByKey("information.theRangeDateMustBeBetweenDays", true).replace(
				"{0}",
				String(parameterTypeValue),
			),
		},
	};

	for (const keyValidatation of Object.keys(obj_validations_Date) as TypeKeysUniqueValidationsDate[]) {
		let skipValidation: boolean = false;

		if (ignoreKeyValidations && ignoreKeyValidations.length > 0) {
			skipValidation = ignoreKeyValidations.includes(keyValidatation);
		}

		if (
			!skipValidation &&
			obj_validations_Date[keyValidatation].condition &&
			obj_validations_Date[keyValidatation].message
		) {
			return {
				messages: [obj_validations_Date[keyValidatation].message],
				code: `VALIDATIONS_DATE - ${keyValidatation}`,
			};
		}
	}
	//#endregion

	return null;
}

export function onLoadDateRangePicker(parameterValue: number, startDate?: Date | null, finalDate?: Date | null) {
	const intialStartDateForm = isNullUndefined(parameterValue) ? initalDateForm() : initalDateForm(parameterValue);
	const firstDateResult = startDate || intialStartDateForm;
	const secondDateResult = finalDate || initalDateForm();

	return {
		firstDateResult,
		secondDateResult,
	};
}
export function onLoadDateRangeBetweenPicker(
	parameterValue: { initialRange: number; finalRange: number },
	startDate?: Date,
	finalDate?: Date,
) {
	const intialStartDateForm = isNullUndefined(parameterValue.initialRange)
		? initalDateForm()
		: initalDateForm(parameterValue.initialRange);
	const intialFinalDateForm = isNullUndefined(parameterValue.finalRange)
		? finalDateForm()
		: finalDateForm(parameterValue.finalRange);

	const firstDateResult = startDate || intialStartDateForm;
	const secondDateResult = finalDate || intialFinalDateForm;

	return {
		firstDateResult,
		secondDateResult,
	};
}

export function isStringDate(stringDate: string) {
	if (typeof stringDate !== "string" || !isNaN(Number(stringDate))) {
		return false;
	}

	return new Date(stringDate).toString() !== "Invalid Date" && !isNaN(new Date(stringDate).getTime());
}
export function isAfterStartFinalDate(startDate: Date, finalDate: Date) {
	const startDateString = !isNullUndefined(startDate) ? startDate.toUTCString() : "";
	const finalDateString = !isNullUndefined(finalDate) ? finalDate.toUTCString() : "";
	const startDateValue = new Date(startDateString);
	const finalDateValue = new Date(finalDateString);

	startDateValue.setHours(0, 0, 1, 0);
	finalDateValue.setHours(23, 59, 59, 999);

	return isAfter(startDateValue, finalDateValue);
}

export function isSameDate(date1: Date | null | undefined | string, date2: Date | null | undefined | string) {
	if (isNullUndefined(date1) || isNullUndefined(date2)) return false;
	const formattedDate1 = typeof date1 === "string" ? new Date(date1) : date1;
	const formattedDate2 = typeof date2 === "string" ? new Date(date2) : date2;

	const dayDate1 = formattedDate1.getDay();
	const monthDate1 = formattedDate1.getMonth();
	const yearDate1 = formattedDate1.getFullYear();

	const dayDate2 = formattedDate2.getDay();
	const monthDate2 = formattedDate2.getMonth();
	const yearDate2 = formattedDate2.getFullYear();

	return dayDate1 === dayDate2 && monthDate1 === monthDate2 && yearDate1 === yearDate2;
}

export function getKeysIgnoreValidationsDate(
	ignoreValidations: boolean,
	startDate: Date | null,
	finalDate: Date | null,
) {
	const keysIgnore = ignoreValidations ? getAllKeysIgnoreValidationsDate() : [];
	const keysValidateStartDate = !isNullUndefined(startDate)
		? getAllKeysIgnoreValidationsDate(["rangeBetweenDays"])
		: [];
	const keysValidateFinalDate = !isNullUndefined(finalDate)
		? ["isInvalidFinalDate", "finalDateGreaterThanCurrentDate"]
		: [];
	const mergedKeysValidate = [...keysValidateStartDate, ...keysValidateFinalDate];
	const keysIgnoreValidationsDateSubmit = keysIgnore.filter((x) => !mergedKeysValidate.includes(x));

	return keysIgnoreValidationsDateSubmit;
}

export function getKeysIgnoreValidationsDateCustom(
	keysToIgnore: TypeKeysValidationsDate[],
	startDate: Date | null,
	finalDate: Date | null,
) {
	const keysIgnore = keysToIgnore;
	const keysValidateStartDate = !isNullUndefined(startDate)
		? getAllKeysIgnoreValidationsDate(["rangeBetweenDays"])
		: [];
	const keysValidateFinalDate = !isNullUndefined(finalDate)
		? ["isInvalidFinalDate", "finalDateGreaterThanCurrentDate"]
		: [];
	const mergedKeysValidate = [...keysValidateStartDate, ...keysValidateFinalDate];
	const keysIgnoreValidationsDateSubmit = keysIgnore.filter((x) => !mergedKeysValidate.includes(x));

	return keysIgnoreValidationsDateSubmit;
}

export const isDateValid = (date: Date) => !isNullUndefined(date) && date instanceof Date && !isNaN(date.getTime());

export const formatStringToISOString = (date: string): string => {
	const [day, month, year] = date.split("/");
	const dataObj = new Date(`${year}-${month}-${day}`);
	const formattedDateClaim = dataObj.toISOString().slice(0, 16);

	return formattedDateClaim;
};

export function getStartandFinalDateByString(dateString: string) {
	let date = new Date(dateString);
	if (isNaN(date.getTime())) {
		date = new Date();
	}

	const startDate = new Date(date);
	startDate.setDate(startDate.getDate() - 2);

	let finalDate = new Date(date);
	finalDate.setDate(finalDate.getDate() + 2);

	const today = new Date();
	if (finalDate > today) {
		finalDate = today;
	}

	return {
		startDate: startDate.toISOString(),
		finalDate: finalDate.toISOString(),
	};
}
